"use client";

import { useEffect, createRef, useState, useRef } from "react";
import { PropTypes } from "prop-types";
import { Box, IconButton, RadioGroup } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { CarouselRoot, SlideContainer } from "./Carousel.styles";
import { Radio } from "@components";
import { useWindowDimensions } from "@utils";

const Carousel = ({ children }) => {
  const slideContainerRef = useRef();
  const [slideRefs, setSlideRefs] = useState([]);
  const [selectedSlide, setSelectedSlide] = useState(0);
  const { windowWidth } = useWindowDimensions();

  const [isContentOverflowing, setIsContentOverflowing] = useState(false);
  const detectOverflow = (slideEl) => {
    if (!slideEl) return;
    setIsContentOverflowing(slideEl.scrollWidth > slideEl.clientWidth);
  };

  useEffect(() => {
    detectOverflow(slideContainerRef.current);
  }, [windowWidth]);

  useEffect(() => {
    setSlideRefs((elRefs) =>
      Array(children?.length)
        .fill()
        .map((_, i) => elRefs[i] || createRef())
    );
  }, [children.length]);

  const scrollToEl = (el) => {
    el.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
inline: "center",
    });
  };

  const handleScrollRight = () => {
    if (selectedSlide === children.length - 1) return;
    scrollToEl(slideRefs[selectedSlide + 1].current);
    setSelectedSlide(selectedSlide + 1);
  };

  const handleScrollLeft = () => {
    if (selectedSlide === 0) return;
    scrollToEl(slideRefs[selectedSlide - 1].current);
    setSelectedSlide(selectedSlide - 1);
  };

  const handleRadioClick = (event) => {
    if (event.target.value === selectedSlide) return;
    const parsedValue = parseInt(event.target.value);
    scrollToEl(slideRefs[parsedValue].current);
    setSelectedSlide(parsedValue);
  };

  return (
    <CarouselRoot>
      <IconButton
        onClick={handleScrollLeft}
        aria-label="previous project"
        size="large"
        sx={{
          position: "absolute",
          top: "50%",
          left: -5,
          zIndex: 100,
          display: isContentOverflowing ? "inline-flex" : "none",
        }}
      >
        <ChevronLeftIcon />
      </IconButton>
      <IconButton
        onClick={handleScrollRight}
        aria-label="next project"
        size="large"
        sx={{
          position: "absolute",
          top: "50%",
          right: -5,
          zIndex: 100,
          display: isContentOverflowing ? "inline-flex" : "none",
        }}
      >
        <ChevronRightIcon />
      </IconButton>
      <SlideContainer
        ref={slideContainerRef}
        component="ul"
        sx={{ ...(isContentOverflowing ? null : { justifyContent: "center" }) }}
      >
        {children.map((child, index) => {
          return (
            <Box
              key={index}
              ref={slideRefs[index]}
              component="li"
              sx={{ all: "unset" }}
            >
              {child}
            </Box>
          );
        })}
      </SlideContainer>
      <RadioGroup
        aria-label="selected-project-card-slide"
        sx={{
          display: isContentOverflowing ? "flex" : "none",
          flexDirection: "row",
          justifyContent: "center",
          width: "100%",
        }}
      >
        {children.map((child, index) => {
          return (
            <Radio
              key={index}
              checked={selectedSlide === index}
              value={index}
              onClick={handleRadioClick}
              inputProps={{
                label: index,
                "aria-label": index,
              }}
              sx={{
                "& .MuiSvgIcon-root": {
                  fontSize: { xs: 30, md: 24 },
                },
              }}
            />
          );
        })}
      </RadioGroup>
    </CarouselRoot>
  );
};

Carousel.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
};

export default Carousel;
